import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Check, Copy, Link2, QrCode } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const SITE = 'https://outlawmedia.com.au';

// Each channel preset fills source + medium; campaign and content stay yours.
const CHANNELS = [
  { key: 'instagram-bio', label: 'Instagram bio', source: 'instagram', medium: 'bio', content: 'bio-link' },
  { key: 'instagram-story', label: 'Instagram story', source: 'instagram', medium: 'story', content: 'story-swipe' },
  { key: 'instagram-post', label: 'Instagram post', source: 'instagram', medium: 'post', content: 'caption-link' },
  { key: 'x-post', label: 'X post', source: 'x', medium: 'post', content: 'tweet' },
  { key: 'x-bio', label: 'X bio', source: 'x', medium: 'bio', content: 'bio-link' },
  { key: 'email', label: 'Email broadcast', source: 'email', medium: 'newsletter', content: 'main-link' },
  { key: 'poster', label: 'Poster / QR code', source: 'poster', medium: 'print', content: 'qr' },
];

const slug = (s) => (s || '')
  .toLowerCase()
  .replace(/[^a-z0-9]+/g, '-')
  .replace(/^-+|-+$/g, '')
  .slice(0, 60);

const PAGES = [
  { path: '/', label: 'Homepage' },
  { path: '/stories', label: 'All stories' },
  { path: '/subscribe', label: 'Subscribe / plans' },
  { path: '/login', label: 'Sign up / login' },
  { path: '/founders', label: 'Founders wall' },
  { path: '/tips', label: 'Tip line' },
];

/** Builds campaign-tagged links for Instagram, X, email and posters so every
 *  click lands in the UTM reports and Plausible. */
const LinkBuilderCard = ({ token }) => {
  const [channel, setChannel] = useState(CHANNELS[0].key);
  const [target, setTarget] = useState('/');
  const [campaign, setCampaign] = useState('');
  const [content, setContent] = useState('');
  const [term, setTerm] = useState('');
  const [articles, setArticles] = useState([]);
  const [copied, setCopied] = useState(false);

  const preset = useMemo(() => CHANNELS.find((c) => c.key === channel) || CHANNELS[0], [channel]);

  useEffect(() => { setContent(preset.content); }, [preset]);

  const loadArticles = useCallback(async () => {
    try {
      const r = await fetch(`${API}/api/admin/articles?limit=50`, { headers: { Authorization: `Bearer ${token}` } });
      const data = await r.json();
      const items = Array.isArray(data) ? data : (data.items || []);
      setArticles(items.filter((a) => a.slug));
    } catch { /* dropdown just shows the standard pages */ }
  }, [token]);

  useEffect(() => { loadArticles(); }, [loadArticles]);

  const url = useMemo(() => {
    const p = new URLSearchParams();
    p.set('utm_source', preset.source);
    p.set('utm_medium', preset.medium);
    p.set('utm_campaign', slug(campaign) || 'general');
    if (slug(content)) p.set('utm_content', slug(content));
    if (slug(term)) p.set('utm_term', slug(term));
    return `${SITE}${target}?${p.toString()}`;
  }, [preset, target, campaign, content, term]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch { /* clipboard blocked - the field is selectable */ }
  };

  const downloadQr = async () => {
    try {
      const r = await fetch(`${API}/api/admin/link-builder/qr?url=${encodeURIComponent(url)}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const blob = await r.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = href;
      a.download = `outlaw-qr-${slug(campaign) || 'general'}.png`;
      a.click();
      URL.revokeObjectURL(href);
    } catch { /* ignore */ }
  };

  return (
    <div className="border border-zinc-800 bg-zinc-900/40 rounded-lg p-4 mb-4" data-testid="link-builder-card">
      <div className="flex items-start gap-3 mb-4">
        <Link2 className="h-5 w-5 text-red-500 mt-0.5" />
        <div>
          <p className="text-white font-black text-sm">Link builder</p>
          <p className="text-zinc-400 text-xs leading-relaxed">
            Build a tagged link for each place you post it. Every click then shows up against the
            right campaign in your subscriber exports and Plausible.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <label className="block">
          <span className="text-[10px] font-black uppercase tracking-[0.25em] text-gray-400">Where you're posting it</span>
          <select
            value={channel}
            onChange={(e) => setChannel(e.target.value)}
            data-testid="link-builder-channel"
            className="mt-1 w-full h-10 bg-zinc-950 border border-zinc-800 rounded px-2 text-sm text-white outline-none focus:border-red-500"
          >
            {CHANNELS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </label>

        <label className="block">
          <span className="text-[10px] font-black uppercase tracking-[0.25em] text-gray-400">Where it should land</span>
          <select
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            data-testid="link-builder-target"
            className="mt-1 w-full h-10 bg-zinc-950 border border-zinc-800 rounded px-2 text-sm text-white outline-none focus:border-red-500"
          >
            {PAGES.map((p) => <option key={p.path} value={p.path}>{p.label}</option>)}
            {articles.length > 0 && (
              <optgroup label="Stories">
                {articles.map((a) => (
                  <option key={a.id || a.slug} value={`/stories/${a.slug}`}>{(a.title || a.slug).slice(0, 70)}</option>
                ))}
              </optgroup>
            )}
          </select>
        </label>

        <label className="block">
          <span className="text-[10px] font-black uppercase tracking-[0.25em] text-gray-400">Campaign name</span>
          <input
            value={campaign}
            onChange={(e) => setCampaign(e.target.value)}
            placeholder="e.g. kaz tax launch"
            data-testid="link-builder-campaign"
            className="mt-1 w-full h-10 bg-zinc-950 border border-zinc-800 rounded px-3 text-sm text-white outline-none focus:border-red-500 placeholder:text-zinc-600"
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="text-[10px] font-black uppercase tracking-[0.25em] text-gray-400">Label (content)</span>
            <input
              value={content}
              onChange={(e) => setContent(e.target.value)}
              data-testid="link-builder-content"
              className="mt-1 w-full h-10 bg-zinc-950 border border-zinc-800 rounded px-3 text-sm text-white outline-none focus:border-red-500"
            />
          </label>
          <label className="block">
            <span className="text-[10px] font-black uppercase tracking-[0.25em] text-gray-400">Keyword (optional)</span>
            <input
              value={term}
              onChange={(e) => setTerm(e.target.value)}
              data-testid="link-builder-term"
              className="mt-1 w-full h-10 bg-zinc-950 border border-zinc-800 rounded px-3 text-sm text-white outline-none focus:border-red-500"
            />
          </label>
        </div>
      </div>

      <div className="rounded border border-zinc-800 bg-black/60 p-3">
        <p className="text-[10px] font-black uppercase tracking-[0.25em] text-gray-500 mb-1.5">Your link</p>
        <p className="text-amber-300 text-xs break-all font-mono leading-relaxed" data-testid="link-builder-output">
          {url}
        </p>
        <div className="flex gap-2 mt-3 flex-wrap">
          <button
            type="button"
            onClick={copy}
            data-testid="link-builder-copy"
            className="h-9 px-4 rounded bg-red-600 hover:bg-red-500 text-white text-[11px] font-black uppercase tracking-[0.2em] inline-flex items-center gap-1.5"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Copied' : 'Copy link'}
          </button>
          <button
            type="button"
            onClick={downloadQr}
            data-testid="link-builder-qr"
            className="h-9 px-4 rounded bg-zinc-900 hover:bg-zinc-800 border border-zinc-700 text-white text-[11px] font-black uppercase tracking-[0.2em] inline-flex items-center gap-1.5"
          >
            <QrCode className="h-3.5 w-3.5" />
            QR for posters
          </button>
        </div>
      </div>
    </div>
  );
};

export default LinkBuilderCard;
